<script>
// ConversationBasicFilter.vue
import wootConstants from 'dashboard/constants/globals';
import { mapGetters } from 'vuex';
import { ref, computed, nextTick, watch } from 'vue';
import FilterItem from './FilterItem.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import NextButton from 'dashboard/components-next/button/Button.vue';

const CHAT_STATUS_FILTER_ITEMS = Object.freeze([
  'open',
  'resolved',
  'pending',
  'snoozed',
  'all',
]);

const SORT_ORDER_ITEMS = Object.freeze([
  'last_activity_at_asc',
  'last_activity_at_desc',
  'created_at_desc',
  'created_at_asc',
  'priority_desc',
  'priority_asc',
  'waiting_since_asc',
  'waiting_since_desc',
]);

export default {
  components: {
    FilterItem,
    NextButton,
  },
  emits: ['changeFilter'],
  setup() {
    const { updateUISettings } = useUISettings();
    const dropdownRef = ref(null);
    const position = ref({ horizontal: 'center', vertical: 'bottom' });
    const showActionsDropdown = ref(false);

    const positionClasses = computed(() => {
      const classes = [];

      // Horizontal positioning
      if (position.value.horizontal === 'left') {
        classes.push('ltr:right-0 rtl:left-0');
      } else if (position.value.horizontal === 'center') {
        classes.push('ltr:left-1/2 ltr:-translate-x-1/2 rtl:right-1/2 rtl:translate-x-1/2');
      } else {
        // right (default)
        classes.push('ltr:left-0 rtl:right-0');
      }

      // Vertical positioning
      if (position.value.vertical === 'top') {
        classes.push('bottom-full', 'mb-2');
      } else {
        // bottom (default)
        classes.push('top-6', 'mt-1');
      }

      return classes.join(' ');
    });

    const detectEdgePosition = async () => {
      await nextTick();

      if (!dropdownRef.value) return;

      const dropdown = dropdownRef.value;
      const rect = dropdown.getBoundingClientRect();
      const padding = 8; // Safety margin from edges

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Get parent element position to calculate relative positioning
      const parent = dropdown.parentElement;
      const parentRect = parent?.getBoundingClientRect();

      if (!parentRect) return;

      // Calculate where dropdown would be positioned for each option
      // and ensure 8px from screen edges

      // Horizontal edge detection
      const leftAlignedRight = parentRect.left + rect.width; // right edge when left-aligned (left-0)
      const rightAlignedLeft = parentRect.right - rect.width; // left edge when right-aligned (right-0)
      const centeredLeft = parentRect.left + parentRect.width / 2 - rect.width / 2;
      const centeredRight = centeredLeft + rect.width;

      // Check if dropdown would overflow right edge when left-aligned
      if (leftAlignedRight > viewportWidth - padding) {
        // Check if right-align would work
        if (rightAlignedLeft >= padding) {
          // Right-align works
          position.value.horizontal = 'left';
        } else if (centeredLeft >= padding && centeredRight <= viewportWidth - padding) {
          // Center works
          position.value.horizontal = 'center';
        } else {
          // Nothing fits perfectly, use right-align as lesser evil
          position.value.horizontal = 'left';
        }
      }
      // Check if dropdown would overflow left edge when right-aligned
      else if (parentRect.left < padding) {
        // Left-align works
        position.value.horizontal = 'right';
      }
      // Default: try to center if there's enough space
      else {
        if (centeredLeft >= padding && centeredRight <= viewportWidth - padding) {
          position.value.horizontal = 'center';
        } else if (parentRect.right < viewportWidth / 2) {
          // Parent is on left side of screen, align dropdown to left (left-0)
          position.value.horizontal = 'right';
        } else {
          // Parent is on right side of screen, align dropdown to right (right-0)
          position.value.horizontal = 'left';
        }
      }

      // Vertical edge detection
      // Check if dropdown would overflow bottom edge
      if (parentRect.bottom + rect.height + padding > viewportHeight) {
        // Position above
        position.value.vertical = 'top';
      } else {
        // Position below (default)
        position.value.vertical = 'bottom';
      }
    };

    // Watch for dropdown visibility changes to recalculate position
    watch(showActionsDropdown, (newVal) => {
      if (newVal) {
        detectEdgePosition();
      }
    });

    return {
      updateUISettings,
      dropdownRef,
      position,
      positionClasses,
      showActionsDropdown,
    };
  },
  data() {
    return {
      chatStatusItems: CHAT_STATUS_FILTER_ITEMS,
      chatSortItems: SORT_ORDER_ITEMS,
    };
  },
  computed: {
    ...mapGetters({
      chatStatusFilter: 'getChatStatusFilter',
      chatSortFilter: 'getChatSortFilter',
    }),
    chatStatus() {
      return this.chatStatusFilter || wootConstants.STATUS_TYPE.OPEN;
    },
    sortFilter() {
      return (
        this.chatSortFilter || wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC
      );
    },
    statusLabel() {
      return this.$t(`CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.${this.chatStatus}.TEXT`);
    },
    hasNonAllStatusFilter() {
      return this.chatStatus !== 'all';
    },
  },
  methods: {
    onTabChange(value) {
      this.$emit('changeFilter', value);
      this.closeDropdown();
    },
    toggleDropdown() {
      this.showActionsDropdown = !this.showActionsDropdown;
    },
    closeDropdown() {
      this.showActionsDropdown = false;
    },
    onChangeFilter(value, type) {
      this.$emit('changeFilter', value, type);
      this.saveSelectedFilter(type, value);
    },
    saveSelectedFilter(type, value) {
      this.updateUISettings({
        conversations_filter_by: {
          status: type === 'status' ? value : this.chatStatus,
          order_by: type === 'sort' ? value : this.sortFilter,
        },
      });
    },
  },
};
</script>

<template>
  <div class="relative flex w-full justify-center">
    <div class="relative inline-flex">
      <NextButton
        v-tooltip.right="$t('CHAT_LIST.SORT_TOOLTIP_LABEL')"
        icon="i-lucide-list-filter"
        size="sm"
        color="slate"
        variant="ghost"
        class="!rounded-full !outline-0 aspect-square !text-base !text-n-slate-11"
        @click="toggleDropdown"
      />
      <!-- Badge indicator when status is not 'all' -->
      <span
        v-if="hasNonAllStatusFilter"
        class="absolute top-0 right-0 w-2 h-2 bg-n-brand rounded-full"
      ></span>
    </div>
    <div
      v-if="showActionsDropdown"
      ref="dropdownRef"
      v-on-clickaway="closeDropdown"
      :class="[
        'absolute dropdown-pane dropdown-pane--open !w-64 !p-6 border border-n-weak bg-white/30 dark:bg-slate-800/20 backdrop-blur-3xl shadow-lg rounded-xl',
        positionClasses
      ]"
    >
      <div class="flex items-center justify-between last:mt-4">
        <span class="text-sm font-medium text-n-slate-12">{{
          $t('CHAT_LIST.CHAT_SORT.STATUS')
        }}</span>
        <FilterItem
          type="status"
          :selected-value="chatStatus"
          :items="chatStatusItems"
          path-prefix="CHAT_LIST.CHAT_STATUS_FILTER_ITEMS"
          @on-change-filter="onChangeFilter"
        />
      </div>
      <div class="flex items-center justify-between last:mt-4">
        <span class="text-sm font-medium text-n-slate-12">{{
          $t('CHAT_LIST.CHAT_SORT.ORDER_BY')
        }}</span>
        <FilterItem
          type="sort"
          :selected-value="sortFilter"
          :items="chatSortItems"
          path-prefix="CHAT_LIST.SORT_ORDER_ITEMS"
          @on-change-filter="onChangeFilter"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
/* Styles removed as we no longer have a button group */
</style>

